import { CartAddEvent } from '@theme/events';

/** Uses the theme's native cart event/section refresh; no parallel cart state. */
class SBCartCrossSell extends HTMLElement {
  connectedCallback() {
    if (this.controller) this.controller.abort();
    this.controller = new AbortController();
    this.addEventListener('change', this.onChange, { signal: this.controller.signal });
    this.addEventListener('click', this.onClick, { signal: this.controller.signal });
    this.addEventListener('toggle', this.onToggle, { capture: true, signal: this.controller.signal });
  }

  disconnectedCallback() {
    this.controller?.abort();
  }

  onToggle = () => {
    const dialog = this.closest('dialog');
    const summary = this.closest('.cart-drawer__summary');
    if (dialog && summary) {
      const isTall = summary.getBoundingClientRect().height > dialog.getBoundingClientRect().height * 0.5;
      dialog.setAttribute('cart-summary-sticky', isTall ? 'false' : 'true');
    }
  };

  onChange = (event) => {
    if (this.uncertain) return;
    const select = event.target.closest('[data-sb-cart-variant]');
    if (!select) return;
    const card = select.closest('[data-sb-cart-card]');
    const option = select.selectedOptions[0];
    const add = card.querySelector('[data-sb-cart-add]');
    add.disabled = !select.value || option.disabled;
    add.textContent = 'Add';
    if (!select.value) return;
    card.querySelector('[data-sb-cart-price]').textContent = option.dataset.price;
    const compare = card.querySelector('[data-sb-cart-compare]');
    compare.textContent = option.dataset.compare || '';
    compare.hidden = !option.dataset.compare;
    const saving = card.querySelector('[data-sb-cart-saving]');
    saving.textContent = option.dataset.saving || '';
    saving.hidden = !option.dataset.saving;
    card.querySelector('[data-sb-cart-status]').textContent = '';
  };

  onClick = async (event) => {
    const button = event.target.closest('[data-sb-cart-add]');
    if (!button || button.disabled || this.busy || this.uncertain) return;
    const card = button.closest('[data-sb-cart-card]');
    const select = card.querySelector('[data-sb-cart-variant]');
    const variantId = Number(select.value);
    if (!Number.isSafeInteger(variantId) || variantId < 1 || select.selectedOptions[0]?.disabled) return;
    const status = card.querySelector('[data-sb-cart-status]');
    this.busy = true;
    button.disabled = true;
    select.disabled = true;
    button.textContent = 'Adding…';
    status.textContent = '';
    let added = false;
    let rejected = false;
    try {
      const sections = [...new Set([...document.querySelectorAll('cart-items-component[data-section-id]')]
        .map((element) => element.dataset.sectionId).filter(Boolean))].slice(0, 5);
      const response = await fetch(this.dataset.addUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }], sections: sections.join(','), sections_url: location.pathname }),
      });
      const result = await response.json();
      if (!response.ok || result.status) {
        // Explicit client rejection is safe to correct. Server/network failures may have committed the add.
        rejected = response.status >= 400 && response.status < 500;
        throw new Error(result.description || result.message || 'This option could not be added. Please try another size.');
      }
      added = true;
      status.textContent = 'Added to your bag.';
      button.textContent = 'Added';
      const cartResponse = await fetch(`${this.dataset.cartUrl}.js`, { headers: { Accept: 'application/json' }, cache: 'no-store' });
      if (!cartResponse.ok) throw new Error('Cart refresh unavailable.');
      const cart = await cartResponse.json();
      document.dispatchEvent(new CartAddEvent(cart, 'sb-cart-cross-sell', {
        source: 'sb-cart-cross-sell', itemCount: cart.item_count, variantId: String(variantId), sections: result.sections,
      }));
    } catch (error) {
      // A successful add must never be repeated because the follow-up refresh failed.
      if (added) {
        status.textContent = 'Added to your bag. Opening your updated bag…';
        window.location.assign(this.dataset.cartUrl);
      } else if (rejected) {
        status.textContent = error.message || 'Unable to add this item. Please try again.';
      } else {
        this.uncertain = true;
        status.textContent = 'Unable to confirm. Review your bag before retrying.';
        card.querySelector('[data-sb-cart-review]').hidden = false;
        this.querySelectorAll('[data-sb-cart-add], [data-sb-cart-variant]').forEach((control) => { control.disabled = true; });
      }
    } finally {
      this.busy = false;
      if (!added && !this.uncertain) {
        select.disabled = false;
        button.disabled = !select.value;
        button.textContent = 'Add';
      } else if (!added) {
        button.textContent = 'Check bag';
      }
    }
  };
}

if (!customElements.get('sb-cart-cross-sell')) customElements.define('sb-cart-cross-sell', SBCartCrossSell);
